import DeleteIcon from "@mui/icons-material/Delete";
import BackButton from "../../../../assets/images/BUTTON_Back.svg";
import {
  Button,
  Divider,
  Grid,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../../components/Loader";
import { showSnackbar } from "../../../../slice/snackbarSlice";
import { NOTIFICATION_MESSAGE } from "../../../../utils/constant";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getActiveWorkflow,
  getWorkflowById,
  getWorkflowMapping,
  retryOutboxBatch,
  saveWorkFlow,
} from "../../../../thunks/workflow";
import AlertComponent from "../../../../components/alert";
import { Api } from "@mui/icons-material";

const WorkflowMapping = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const instructionTypeId = location?.state?.instructionTypeId;
  const newWorkflowName = location?.state?.workflowName;

  const [isLoader, setLoader] = useState(false);
  const [isError, setIsError] = useState(false);
  const [activeWorkflowData, setActiveWorkflowData] = useState({});
  const [hoveredMatch, setHoveredMatch] = useState(null);
  const [workflowMappingData, setWorkflowMappingData] = useState({});
  const [newWorkflowData, setNewWorkflowData] = useState({});
  const [isMappedWorkflow, setMappedWorkflow] = useState(false);

  // States from AutoMappingVisualizer logic
  const { mappings = [], summary } = workflowMappingData || {};

  const [manualMatches, setManualMatches] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [unmatchedSource, setUnmatchedSource] = useState([]);
  const [unmatchedTarget, setUnmatchedTarget] = useState([]);

  const lookups = useSelector((state) => state?.lookupData);
  const WorkflowStatus = lookups?.WorkFlowStatus;

  let WorkflowStatusData = WorkflowStatus?.find((d) => {
    return d.value === "active";
  });

  const getActiveWorkflowData = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        getActiveWorkflow({
          query: {
            isActive: true,
            workFlowStatusId: WorkflowStatusData?.id || null,
            instructionTypeId: instructionTypeId || null,
          },
        })
      ).unwrap();
      setActiveWorkflowData(response?.[0]);
    } catch (error) {
      setActiveWorkflowData([]);
      dispatch(
        showSnackbar({
          show: true,
          message: error?.message || NOTIFICATION_MESSAGE.SOMETHING_WENT_WRONG,
          severity: "error",
        })
      );
    } finally {
      setLoader(false);
    }
  };

  const getWorkflowMappingData = async () => {
    try {
      setLoader(true);
      const response = await dispatch(getWorkflowMapping(id)).unwrap();
      setWorkflowMappingData(response || {});
      setUnmatchedSource(response?.unmatchedSourceTasks || []);
      setUnmatchedTarget(response?.unmatchedTargetTasks || []);
    } catch (error) {
      setWorkflowMappingData({});
      dispatch(
        showSnackbar({
          show: true,
          message: error?.message || NOTIFICATION_MESSAGE.SOMETHING_WENT_WRONG,
          severity: "error",
        })
      );
    } finally {
      setLoader(false);
    }
  };

  // Helper function to get the appropriate sequence ID for display
  const getSequenceId = (task) => {
    return (
      task.newMilestoneSequenceId ||
      task.milestoneSequenceId ||
      task.globalSequenceId
    );
  };

  // Validation function to check if mapping is allowed
  const validateMapping = (sourceTask, targetTask) => {
    const sourceIsSubtask = sourceTask?.isSubTask;
    const targetIsSubtask = targetTask?.isSubTask;

    // Check if one is subtask and the other is a main task
    if (sourceIsSubtask !== targetIsSubtask) {
      dispatch(
        showSnackbar({
          show: true,
          message: NOTIFICATION_MESSAGE.WORKFLOW_MAPPING,
          severity: "error",
        })
      );

      return {
        isValid: false,
        message:
          "Mapping a subtask to a main task or vice versa is not allowed.",
      };
    }

    return { isValid: true };
  };

  // Get workflow data by ID
  const getWorkflowDataByID = useCallback(async () => {
    if (!id) return;

    try {
      setLoader(true);

      const response = await dispatch(getWorkflowById(id)).unwrap();

      if (response) {
        setNewWorkflowData(response);
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          show: true,
          message:
            error?.message ||
            NOTIFICATION_MESSAGE?.SOMETHING_WENT_WRONG ||
            "Something went wrong",
          severity: "error",
        })
      );
    } finally {
      setLoader(false);
    }
  }, [id]);
  // Auto-match when both source and target are selected
  useEffect(() => {
    if (selectedSource && selectedTarget) {
      // Validate the mapping before proceeding
      // Allow only subTask to subTask connections
      if (selectedSource.isSubTask !== selectedTarget.isSubTask) {
        // Show error notification
        dispatch(
          showSnackbar({
            show: true,
            message: "Main Task cannot be mapped to a Subtask, and vice versa.",
            severity: "error",
          })
        );

        // Reset selections without proceeding
        setSelectedSource(null);
        setSelectedTarget(null);
        return;
      }

      // Proceed with valid mapping using functional updates
      setManualMatches((prev) => [
        ...prev,
        { sourceInfo: selectedSource, targetInfo: selectedTarget },
      ]);
      setUnmatchedSource((prev) =>
        prev.filter((task) => task.id !== selectedSource.id)
      );
      setUnmatchedTarget((prev) =>
        prev.filter((task) => task.id !== selectedTarget.id)
      );
      setSelectedSource(null);
      setSelectedTarget(null);
    }
  }, [selectedSource, selectedTarget]);

  useEffect(() => {
    getActiveWorkflowData();
    getWorkflowMappingData();
    getWorkflowDataByID();
  }, []);

  // Handle removing manual matches
  const handleRemoveMatch = (matchIndex) => {
    const matchToRemove = manualMatches[matchIndex];
    setUnmatchedSource([...unmatchedSource, matchToRemove.sourceInfo]);
    setUnmatchedTarget([...unmatchedTarget, matchToRemove.targetInfo]);
    setManualMatches(manualMatches.filter((_, index) => index !== matchIndex));
  };
  // Trigger batch processing Api
  const handleRetryOutboxBatch = async (id) => {
    try {
      setLoader(true);
      let res = await dispatch(retryOutboxBatch(id)).unwrap();
      if (res) {
        dispatch(
          showSnackbar({
            show: true,
            message: NOTIFICATION_MESSAGE.OUTBOX_BATCH_RETRY_MSG,
            severity: "success",
          })
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `${error?.message}`,
          severity: "error",
        })
      );
    } finally {
      setLoader(false);
    }
  };
  //final submit
  const handleSubmit = async () => {
    try {
      setMappedWorkflow(false);
      setLoader(true);

      const payload = {
        id: id || null,
        name: newWorkflowData?.name || "",
        description: newWorkflowData?.description || "",
        instructionTypeId: newWorkflowData?.instructionTypeId || null,
        workFlowStatusId: WorkflowStatusData?.id || null,
        metaReactFlow: newWorkflowData?.metaReactFlow || {},
        isActive: true,
        backendFlow: newWorkflowData?.backedFlow || {},
        milestonSequence: newWorkflowData?.milestoneSequence || [],
        updateMappingOfWorkflowDto: {
          manualMapped: manualMatches || [],
          mappings: workflowMappingData?.mappings || [],
          unmatchedSourceTasks: workflowMappingData?.unmatchedSourceTasks || [],
          unmatchedTargetTasks: workflowMappingData?.unmatchedTargetTasks || [],
          summary: workflowMappingData?.summary || {},
          message: workflowMappingData?.message || "Mapping data submitted",
          success: workflowMappingData?.success ?? true,
          sourceWorkflowId: workflowMappingData?.sourceWorkflowId || null,
          targetWorkflowId: workflowMappingData?.targetWorkflowId || null,
        },
      };
      const res = await dispatch(saveWorkFlow(payload)).unwrap();
      if (res) {
        //trigger api to progress batch
        handleRetryOutboxBatch(res?.instructionMapped?.batchId);
        dispatch(
          showSnackbar({
            message: "Workflow mapping submitted successfully",
            severity: "success",
          })
        );
        // navigate(`/app/manage/configure-task/workflowMapping/status`);
        navigate("/app/manage/task/list");
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          show: true,
          message: error?.message || NOTIFICATION_MESSAGE.SOMETHING_WENT_WRONG,
          severity: "error",
        })
      );
    } finally {
      setLoader(false);
      setMappedWorkflow(false);
    }
  };

  // Create task mapping categories for display
  const taskMappingCategory = [
    {
      id: 1,
      category: "Matched Automatic",
      tasks: mappings.map((mapping, index) => ({
        id: index + 1,
        name: mapping.sourceInfo.name,
        actor: mapping.sourceInfo.actor,
        targetName: mapping.targetInfo.name,
        targetActor: mapping.targetInfo.actor,
        isMapping: true,
        sourceInfo: mapping.sourceInfo,
        targetInfo: mapping.targetInfo,
      })),
    },
    {
      id: 2,
      category: "Matched Manual",
      tasks: manualMatches.map((match, index) => ({
        id: `manual-${index}`,
        name: match.sourceInfo.name,
        actor: match.sourceInfo.actor,
        targetName: match.targetInfo.name,
        targetActor: match.targetInfo.actor,
        isMapping: true,
        sourceInfo: match.sourceInfo,
        targetInfo: match.targetInfo,
        matchIndex: index,
      })),
    },
  ];

  const handleTaskClick = (task, isSource) => {
    if (isSource) {
      setSelectedSource(selectedSource?.id === task.id ? null : task);
    } else {
      setSelectedTarget(selectedTarget?.id === task.id ? null : task);
    }
  };

  const getTaskBorderStyle = (category, task, isSelected) => {
    if (category === "Matched Manual") {
      return "1px dashed #01ce3f";
    } else if (category === "Matched Automatic") {
      return "1px solid #01ce3f";
    }
    return "1px solid lightGrey";
  };

  const getTaskBackgroundColor = (task, isSource) => {
    if (isSource && selectedSource?.id === task.id) {
      return "#01ce3f";
    } else if (!isSource && selectedTarget?.id === task.id) {
      return "#e5faeb";
    }
    return "transparent";
  };

  const renderTaskCard = (
    task,
    isSource,
    category = null,
    matchIndex = null
  ) => {
    const isSelected = isSource
      ? selectedSource?.id === task.id
      : selectedTarget?.id === task.id;
    const isClickable = category === "Unmatched";

    return (
      <Box
        sx={{
          textAlign: "center",
          border: category
            ? getTaskBorderStyle(category, task, isSelected)
            : isSelected && !isSource
            ? "1px solid #e5faeb"
            : "2px solid #01ce3f",
          backgroundColor: isClickable
            ? getTaskBackgroundColor(task, isSource)
            : "transparent",
          borderRadius: "6px",
          py: 2,
          px: 1,
          cursor: isClickable ? "pointer" : "default",
          position: "relative",
          "&:hover":
            isClickable && !isSource
              ? { backgroundColor: "#e5faeb", border: "1px solid #e5faeb" }
              : { backgroundColor: "#01ce3f" },
        }}
        onClick={() => isClickable && handleTaskClick(task, isSource)}
        onMouseEnter={() =>
          category === "Matched Manual" && setHoveredMatch(matchIndex)
        }
        onMouseLeave={() =>
          category === "Matched Manual" && setHoveredMatch(null)
        }
      >
        {category === "Matched Manual" && hoveredMatch === matchIndex && (
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              backgroundColor: "#dc2626",
              color: "white",
              "&:hover": { backgroundColor: "#b91c1c" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveMatch(matchIndex);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <Typography
          className="normal_normal_14_Manrope dark_grey"
          fontWeight="bold"
        >
          {task?.newTaskId} {task?.name}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {task?.isSubTask && " • Subtask"}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {/* Dialog for Drop Task Confirmation */}
      {isMappedWorkflow && (
        <AlertComponent
          open={isMappedWorkflow}
          handleSuccess={handleSubmit}
          handleClose={() => setMappedWorkflow(false)}
          message="Switching the workflow is an irreversible process. Are you sure you want to continue?"
        />
      )}
      <Grid
        container
        height={"100vh"}
        sx={{ overflow: "auto" }}
        className="dashboard-scrollbar"
      >
        {/* Loader Component */}
        {isLoader && <Loader />}

        {/* Header */}
        <Grid
          container
          alignItems="center"
          sx={{
            width: "100vw",
            backgroundColor: "#e5faeb",
            padding: 2,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          {/* Back Button */}
          <Grid item xs={2} display="flex" justifyContent="flex-start">
            <img
              src={BackButton}
              alt="Back"
              onClick={() => navigate(`/app/manage/configure-task/${id}`)}
              style={{
                cursor: "pointer",
                marginLeft: 8,
                height: 24,
              }}
            />
          </Grid>

          {/* Title */}
          <Grid item xs={8} textAlign="center">
            <Typography className="serif_display_regular_20 dark_grey">
              Workflow Task Mapping
            </Typography>
          </Grid>
        </Grid>

        {/* Workflow Status */}
        <Grid
          item
          xs={12}
          display={"flex"}
          justifyContent={"space-between"}
          mt={3}
          marginRight={10}
          marginLeft={10}
        >
          <Grid item xs={2}>
            <Typography className="normal_normal_16_Manrope green">
              ACTIVE
            </Typography>
            <Typography className="normal_normal_14_Manrope dark_grey">
              {activeWorkflowData?.name || "--"}
            </Typography>
          </Grid>
          <Grid item xs={2} textAlign={"end"}>
            <Typography className="normal_normal_16_Manrope green">
              NEW
            </Typography>
            <Typography className="normal_normal_14_Manrope dark_grey">
              {newWorkflowData?.name || "--"}
            </Typography>
          </Grid>
        </Grid>

        {/* Task Categories - Matched Automatic and Manual */}
        {taskMappingCategory?.map((item, index) => (
          <React.Fragment key={index}>
            <Grid item xs={12} mt={2} mb={2} marginRight={10} marginLeft={10}>
              <Divider
                sx={{
                  "&::before, &::after": {
                    borderColor: "#3d4740",
                  },
                }}
              >
                <Typography className="serif_display_regular_18 dark_grey">
                  {item?.category}
                </Typography>
              </Divider>
            </Grid>

            {item?.tasks?.length > 0 ? (
              <>
                {item?.tasks.map((task, taskIndex) => (
                  <Grid
                    container
                    item
                    xs={12}
                    spacing={3}
                    key={taskIndex}
                    justifyContent={"center"}
                    mb={2}
                    marginRight={10}
                    marginLeft={10}
                  >
                    <Grid item xs={5}>
                      {renderTaskCard(
                        task?.sourceInfo || task,
                        true,
                        item?.category,
                        task?.matchIndex
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={2}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography variant="h6" color="#00ce3f">
                        →
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      {renderTaskCard(
                        task?.targetInfo || {
                          name: task?.targetName,
                          actor: task?.targetActor,
                        },
                        false,
                        item?.category,
                        task?.matchIndex
                      )}
                    </Grid>
                  </Grid>
                ))}
              </>
            ) : (
              <Grid item xs={12} textAlign={"center"}>
                <Typography className="serif_display_regular_18 red">
                  {`No ${item?.category} Task Found`}
                </Typography>
              </Grid>
            )}
          </React.Fragment>
        ))}

        {/* Unmatched Tasks Section */}
        <Grid item xs={12} mt={2} mb={2} marginRight={10} marginLeft={10}>
          <Divider
            sx={{
              "&::before, &::after": {
                borderColor: "#3d4740",
              },
            }}
          >
            <Typography className="serif_display_regular_18 dark_grey">
              Unmatched Tasks
            </Typography>
          </Divider>
        </Grid>

        {unmatchedSource?.length > 0 || unmatchedTarget?.length > 0 ? (
          <Grid
            container
            item
            xs={12}
            spacing={3}
            marginRight={10}
            display={"flex"}
            justifyContent={"space-between"}
            marginLeft={10}
          >
            {/* Source Tasks */}
            <Grid item xs={5}>
              <Typography
                className="serif_display_regular_18"
                color="#3d4740"
                gutterBottom
                textAlign="center"
              >
                Source Tasks
              </Typography>
              <Grid
                container
                spacing={2}
                mt={1}
                display={"flex"}
                alignItems={"center"}
              >
                {unmatchedSource?.map((task, index) => (
                  <Grid item xs={12} key={task?.id}>
                    {renderTaskCard(task, true, "Unmatched")}
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Target Tasks */}
            <Grid item xs={5}>
              <Typography
                className="serif_display_regular_18"
                color="#3d4740"
                gutterBottom
                textAlign="center"
              >
                Target Tasks
              </Typography>
              <Grid container spacing={2} mt={1}>
                {unmatchedTarget?.map((task, index) => (
                  <Grid item xs={12} key={task?.id}>
                    {renderTaskCard(task, false, "Unmatched")}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12} textAlign={"center"}>
            <Typography className="serif_display_regular_18 red">
              No Unmatched Tasks Found
            </Typography>
          </Grid>
        )}

        {/* Submit Button */}
        <Grid
          item
          xs={12}
          display={"flex"}
          justifyContent={"center"}
          mt={4}
          mb={4}
        >
          <Button
            className="save_button"
            // onClick={handleSubmit}
            onClick={() => setMappedWorkflow(true)}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default WorkflowMapping;
